import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'
import { setSessionRefreshHandler } from './api/http'
import { logout, refresh, updateLocale } from './auth/authApi'
import { isAdminUser } from './auth/authRole'
import { captureAmazonOAuthCallbackFromUrl, clearAmazonOAuthCallback, hasStoredAmazonOAuthCallback } from './channels/amazonOAuthSession'
import { captureSallaOAuthCallbackFromUrl, clearSallaOAuthCallback, hasStoredSallaOAuthCallback } from './channels/sallaOAuthSession'
import { captureSallaInvitation, clearSallaInvitation } from './channels/sallaInvitationSession'
import { captureZidOAuthCallbackFromUrl, clearZidOAuthCallback, hasStoredZidOAuthCallback } from './channels/zidOAuthSession'
import { captureZohoOAuthCallbackFromUrl, clearZohoOAuthCallback, hasStoredZohoOAuthCallback } from './inventory/zohoOAuthSession'
import type { InventorySingleItemImportResponse } from './inventory/types'
import { getOrderSummary } from './orders/ordersApi'
import type { AuthResponse, AuthUser } from './auth/types'
import { AppShell, type NavPage } from './components/shell/AppShell'
import { SharedImportsProvider } from './channels/SharedImports'
import { useSharedImports } from './channels/importsContext'
import { AdminShell, type AdminNavPage } from './components/admin/AdminShell'
import { setLocale, type Locale } from './i18n'
import { LandingPage } from './pages/public/LandingPage'
import { PublicLegalPage } from './pages/public/PublicLegalPage'
import { FaqPage } from './pages/public/FaqPage'
import { SupportPage } from './pages/public/SupportPage'
import type { PublicDestination } from './pages/public/PublicPageLayout'
import { HomePage } from './pages/HomePage'
import { SalesAnalyticsPage } from './pages/SalesAnalyticsPage'
import { ProductsPage } from './pages/ProductsPage'
import { ProductDetailPage } from './pages/ProductDetailPage'
import { ProductsEmptyPage } from './pages/ProductsEmptyPage'
import { ProductFormPage } from './pages/ProductFormPage'
import { OrdersPage } from './pages/OrdersPage'
import { OrderDetailPage, type OrderDetailTab } from './pages/OrderDetailPage'
import type { OrderSummary } from './orders/types'
import { ChannelsPage } from './pages/ChannelsPage'
import { ChannelConnectionDetailPage } from './pages/ChannelConnectionDetailPage'
import { ChannelUnmappedListingsPage } from './pages/ChannelUnmappedListingsPage'
import { ChannelInventoryVariancePage } from './pages/ChannelInventoryVariancePage'
import { StockAllocationRulesPage } from './pages/StockAllocationRulesPage'
import { InventoryPage } from './pages/InventoryPage'
import { TlaaqInventoryDetailPage } from './pages/TlaaqInventoryDetailPage'
import { InventoryZohoDetailPage } from './pages/InventoryZohoDetailPage'
import { InventoryZohoOrderAuditPage } from './pages/InventoryZohoOrderAuditPage'
import { InventoryZohoPullRunDetailPage } from './pages/InventoryZohoPullRunDetailPage'
import { SyncRuleWizardPage } from './pages/SyncRuleWizardPage'
import { OrderRoutingPage } from './pages/OrderRoutingPage'
import { WarehousesPage } from './pages/WarehousesPage'
import { TeamPage } from './pages/TeamPage'
import { OnboardingPage } from './pages/OnboardingPage'
import { AmazonOAuthPage } from './pages/AmazonOAuthPage'
import { SallaOAuthPage } from './pages/SallaOAuthPage'
import { SallaInvitationPage } from './pages/SallaInvitationPage'
import { ZidOAuthPage } from './pages/ZidOAuthPage'
import { ZohoOAuthPage } from './pages/ZohoOAuthPage'
import { AuthLayout } from './pages/auth/AuthLayout'
import { LoginPage } from './pages/auth/LoginPage'
import { RegisterPage } from './pages/auth/RegisterPage'
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage'
import { AcceptInvitationPage } from './pages/auth/AcceptInvitationPage'
import { AdminHomePage } from './pages/admin/AdminHomePage'
import { AdminOrganizationsPage } from './pages/admin/AdminOrganizationsPage'
import { AdminOrganizationDetailPage } from './pages/admin/AdminOrganizationDetailPage'
import { AdminApiCallsPage } from './pages/admin/AdminApiCallsPage'
import { AdminScheduledTasksPage } from './pages/admin/AdminScheduledTasksPage'
import { AdminProviderProxyPage } from './pages/admin/AdminProviderProxyPage'
import { AdminStockAllocationDiagnosticsPage } from './pages/admin/AdminStockAllocationDiagnosticsPage'
import type { AdminProviderProxySurface } from './admin/types'
import './App.css'

type AuthState =
  | { status: 'checking' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; accessToken: string; user: AuthUser }

type Screen =
  | 'landing'
  | 'privacy'
  | 'terms'
  | 'faq'
  | 'support'
  | NavPage
  | 'tlaaq-inventory-detail'
  | 'zoho-detail'
  | 'zoho-order-audit'
  | 'zoho-pull-run-detail'
  | 'sync-wizard'
  | 'order-routing'
  | 'product-detail'
  | 'product-new'
  | 'product-edit'
  | 'products-empty'
  | 'order-detail'
  | 'channel-detail'
  | 'unmapped-channel-listings'
  | 'channel-inventory-variance'
  | 'stock-allocation-rules'
  | 'stock-allocation-activity'
  | 'onboarding'
  | 'zid-oauth'
  | 'salla-oauth'
  | 'salla-invitation'
  | 'amazon-oauth'
  | 'zoho-oauth'
  | 'login'
  | 'register'
  | 'forgot-password'
  | 'reset-password'
  | 'accept-invitation'
  | 'admin-home'
  | 'admin-organizations'
  | 'admin-organization-detail'
  | 'admin-api-calls'
  | 'admin-stock-allocation-diagnostics'
  | 'admin-scheduled-tasks'
  | 'admin-provider-proxy'

type OAuthScreen = Extract<Screen, 'zid-oauth' | 'salla-oauth' | 'amazon-oauth' | 'zoho-oauth'>
type AdminScreen = Extract<Screen, 'admin-home' | 'admin-organizations' | 'admin-organization-detail' | 'admin-api-calls' | 'admin-stock-allocation-diagnostics' | 'admin-scheduled-tasks' | 'admin-provider-proxy'>

const SHELL_SCREENS = new Set<Screen>([
  'home', 'analytics', 'orders', 'products', 'channels', 'inventory', 'warehouses',
  'team', 'tlaaq-inventory-detail', 'zoho-detail', 'zoho-order-audit', 'zoho-pull-run-detail', 'sync-wizard', 'order-routing', 'product-detail', 'product-new', 'product-edit', 'products-empty', 'channel-detail', 'unmapped-channel-listings', 'channel-inventory-variance', 'stock-allocation-rules', 'stock-allocation-activity',
  'order-detail',
])

const AUTH_SCREENS = new Set<Screen>([
  'login', 'register', 'forgot-password', 'reset-password', 'accept-invitation',
])

const PUBLIC_SCREENS = new Set<Screen>([
  'privacy', 'terms', 'faq', 'support',
])

const ADMIN_SCREENS = new Set<Screen>([
  'admin-home', 'admin-organizations', 'admin-organization-detail', 'admin-api-calls', 'admin-stock-allocation-diagnostics', 'admin-scheduled-tasks', 'admin-provider-proxy',
])

const PAGE_META: Record<NavPage, { titleKey: string; subtitleKey?: string; badgeKey?: string }> = {
  home:       { titleKey: 'app.meta.home.title',       subtitleKey: 'app.meta.home.subtitle' },
  analytics:  { titleKey: 'app.meta.analytics.title',  subtitleKey: 'app.meta.analytics.subtitle' },
  orders:     { titleKey: 'app.meta.orders.title' },
  products:   { titleKey: 'app.meta.products.title',   badgeKey: 'app.meta.products.badge' },
  channels:   { titleKey: 'app.meta.channels.title' },
  inventory:  { titleKey: 'app.meta.inventory.title' },
  warehouses: { titleKey: 'app.meta.warehouses.title' },
  team:       { titleKey: 'app.meta.team.title' },
}

const ADMIN_META: Record<AdminScreen, { titleKey: string; subtitleKey?: string }> = {
  'admin-home': { titleKey: 'admin.meta.home.title', subtitleKey: 'admin.meta.home.subtitle' },
  'admin-organizations': { titleKey: 'admin.meta.organizations.title', subtitleKey: 'admin.meta.organizations.subtitle' },
  'admin-organization-detail': { titleKey: 'admin.meta.organizationDetail.title', subtitleKey: 'admin.meta.organizationDetail.subtitle' },
  'admin-api-calls': { titleKey: 'admin.meta.apiCalls.title', subtitleKey: 'admin.meta.apiCalls.subtitle' },
  'admin-stock-allocation-diagnostics': { titleKey: 'admin.meta.stockDiagnostics.title', subtitleKey: 'admin.meta.stockDiagnostics.subtitle' },
  'admin-scheduled-tasks': { titleKey: 'admin.meta.scheduledTasks.title', subtitleKey: 'admin.meta.scheduledTasks.subtitle' },
  'admin-provider-proxy': { titleKey: 'admin.meta.providerProxy.title', subtitleKey: 'admin.meta.providerProxy.subtitle' },
}

const SUB_META: Partial<Record<Screen, { titleKey: string; subtitleKey?: string }>> = {
  'tlaaq-inventory-detail': { titleKey: 'app.meta.tlaaqInventoryDetail.title', subtitleKey: 'app.meta.tlaaqInventoryDetail.subtitle' },
  'zoho-detail':   { titleKey: 'app.meta.zohoDetail.title', subtitleKey: 'app.meta.zohoDetail.subtitle' },
  'zoho-order-audit': { titleKey: 'app.meta.zohoOrderAudit.title', subtitleKey: 'app.meta.zohoOrderAudit.subtitle' },
  'zoho-pull-run-detail': { titleKey: 'app.meta.zohoPullRunDetail.title', subtitleKey: 'app.meta.zohoPullRunDetail.subtitle' },
  'sync-wizard':   { titleKey: 'app.meta.syncWizard.title', subtitleKey: 'app.meta.syncWizard.subtitle' },
  'order-routing': { titleKey: 'app.meta.orderRouting.title', subtitleKey: 'app.meta.orderRouting.subtitle' },
  'product-detail':{ titleKey: 'app.meta.productDetail.title' },
  'order-detail': { titleKey: 'pages.orders.detail.title' },
  'product-new':   { titleKey: 'app.meta.productNew.title', subtitleKey: 'app.meta.productNew.subtitle' },
  'product-edit':  { titleKey: 'app.meta.productEdit.title', subtitleKey: 'app.meta.productEdit.subtitle' },
  'products-empty':{ titleKey: 'app.meta.productsEmpty.title', subtitleKey: 'app.meta.productsEmpty.subtitle' },
  'channel-detail':{ titleKey: 'app.meta.channelDetail.title', subtitleKey: 'app.meta.channelDetail.subtitle' },
  'unmapped-channel-listings':{ titleKey: 'app.meta.unmappedChannelListings.title', subtitleKey: 'app.meta.unmappedChannelListings.subtitle' },
  'channel-inventory-variance':{ titleKey: 'app.meta.channelInventoryVariance.title', subtitleKey: 'app.meta.channelInventoryVariance.subtitle' },
  'stock-allocation-rules':{ titleKey: 'app.meta.stockAllocationRules.title', subtitleKey: 'app.meta.stockAllocationRules.subtitle' },
  'stock-allocation-activity':{ titleKey: 'app.meta.stockAllocationActivity.title', subtitleKey: 'app.meta.stockAllocationActivity.subtitle' },
}

type HistoryMode = 'push' | 'replace'

type RouteParams = {
  orderId?: number | null
  orderTab?: OrderDetailTab | null
  productId?: number | null
  zohoConnectionId?: number | null
  zohoPolicyId?: number | null
  zohoPullRunId?: number | null
  sourceOrderId?: number | null
  channelConnectionId?: number | null
  adminOrganizationId?: number | null
  adminConnectionId?: number | null
  adminProxySurface?: AdminProviderProxySurface | null
}

type AppRoute = RouteParams & { screen: Screen }

type NavigateToScreen = (screen: Screen, historyMode?: HistoryMode, params?: RouteParams) => void

const STATIC_SCREEN_PATHS: Partial<Record<Screen, string>> = {
  landing: '/',
  privacy: '/privacy',
  terms: '/terms',
  faq: '/faq',
  support: '/support',
  home: '/app',
  analytics: '/analytics/sales',
  orders: '/orders',
  products: '/products',
  channels: '/channels',
  'channel-inventory-variance': '/channels/inventory-variance',
  inventory: '/inventory',
  'tlaaq-inventory-detail': '/inventory/tlaaq',
  warehouses: '/warehouses',
  team: '/team',
  'zoho-detail': '/inventory/zoho',
  'product-new': '/products/new',
  'products-empty': '/products/empty',
  onboarding: '/onboarding',
  'zid-oauth': '/zid-oauth',
  'salla-oauth': '/salla-oauth',
  'salla-invitation': '/salla-invitation',
  'amazon-oauth': '/amazon-oauth',
  'zoho-oauth': '/zoho-oauth',
  login: '/login',
  register: '/register',
  'forgot-password': '/forgot-password',
  'reset-password': '/reset-password',
  'accept-invitation': '/accept-invitation',
  'admin-home': '/admin',
  'admin-organizations': '/admin/organizations',
  'admin-api-calls': '/admin/api-calls',
  'admin-stock-allocation-diagnostics': '/admin/stock-allocation-diagnostics',
  'admin-scheduled-tasks': '/admin/scheduled-tasks',
  'admin-provider-proxy': '/admin/provider-proxy',
}

const STATIC_PATH_SCREENS: Record<string, Screen> = {
  '/': 'landing',
  '/privacy': 'privacy',
  '/terms': 'terms',
  '/faq': 'faq',
  '/support': 'support',
  '/app': 'home',
  '/analytics/sales': 'analytics',
  '/login': 'login',
  '/orders': 'orders',
  '/products': 'products',
  '/channels': 'channels',
  '/inventory': 'inventory',
  '/inventory/tlaaq': 'tlaaq-inventory-detail',
  '/warehouses': 'warehouses',
  '/team': 'team',
  // Billing and settings are intentionally disabled in production until they are backed by persisted data.
  '/billing': 'home',
  '/settings': 'home',
  '/inventory/zoho': 'zoho-detail',
  // Legacy pseudo-routes from the initial refresh fix. They are kept only so
  // old URLs can canonicalize to a safe parent page instead of rendering home.
  '/inventory/zoho/sync-rule': 'sync-wizard',
  '/products/detail': 'product-detail',
  '/products/new': 'product-new',
  '/products/edit': 'product-edit',
  '/products/empty': 'products-empty',
  '/channels/unmapped-listings': 'unmapped-channel-listings',
  '/channels/inventory-variance': 'channel-inventory-variance',
  '/channels/stock-allocation-rules': 'stock-allocation-rules',
  '/channels/stock-allocation-activity': 'stock-allocation-activity',
  '/onboarding': 'onboarding',
  '/zid-oauth': 'zid-oauth',
  '/salla-oauth': 'salla-oauth',
  '/salla-invitation': 'salla-invitation',
  '/amazon-oauth': 'amazon-oauth',
  '/zoho-oauth': 'zoho-oauth',
  '/register': 'register',
  '/forgot-password': 'forgot-password',
  '/reset-password': 'reset-password',
  '/accept-invitation': 'accept-invitation',
  '/admin': 'admin-home',
  '/admin/organizations': 'admin-organizations',
  '/admin/api-calls': 'admin-api-calls',
  '/admin/stock-allocation-diagnostics': 'admin-stock-allocation-diagnostics',
  '/admin/scheduled-tasks': 'admin-scheduled-tasks',
  '/admin/provider-proxy': 'admin-provider-proxy',
}

function isAdminScreen(screen: Screen): screen is AdminScreen {
  return ADMIN_SCREENS.has(screen)
}

function activeAdminPage(screen: AdminScreen): AdminNavPage {
  if (screen === 'admin-stock-allocation-diagnostics') return 'admin-api-calls'
  return screen === 'admin-organization-detail' ? 'admin-organizations' : screen
}

function activeNavPage(screen: Screen): NavPage {
  if (screen === 'order-detail') return 'orders'
  if (screen === 'tlaaq-inventory-detail' || screen === 'zoho-detail' || screen === 'zoho-order-audit' || screen === 'zoho-pull-run-detail' || screen === 'sync-wizard' || screen === 'order-routing') return 'inventory'
  if (screen === 'channel-detail' || screen === 'unmapped-channel-listings' || screen === 'channel-inventory-variance' || screen === 'stock-allocation-rules' || screen === 'stock-allocation-activity') return 'channels'
  if (screen === 'product-detail' || screen === 'product-new' || screen === 'product-edit' || screen === 'products-empty') return 'products'
  if (SHELL_SCREENS.has(screen)) return screen as NavPage
  return 'home'
}

function AdminContent({
  screen,
  navigate,
  accessToken,
  selectedAdminOrganizationId,
  selectedAdminConnectionId,
  selectedAdminProxySurface,
}: {
  screen: AdminScreen
  navigate: NavigateToScreen
  accessToken: string
  selectedAdminOrganizationId: number | null
  selectedAdminConnectionId: number | null
  selectedAdminProxySurface: AdminProviderProxySurface | null
}) {
  switch (screen) {
    case 'admin-home':
      return <AdminHomePage accessToken={accessToken} onNavigate={(target) => navigate(target)} />
    case 'admin-organizations':
      return <AdminOrganizationsPage accessToken={accessToken} onOpenOrganization={(adminOrganizationId) => navigate('admin-organization-detail', 'push', { adminOrganizationId })} />
    case 'admin-organization-detail':
      return (
        <AdminOrganizationDetailPage
          accessToken={accessToken}
          organizationId={selectedAdminOrganizationId}
          onBack={() => navigate('admin-organizations')}
          onOpenProxy={(adminProxySurface, adminOrganizationId, adminConnectionId) => navigate('admin-provider-proxy', 'push', { adminProxySurface, adminOrganizationId, adminConnectionId })}
        />
      )
    case 'admin-api-calls':
      return (
        <AdminApiCallsPage
          accessToken={accessToken}
          onOpenOrganization={(adminOrganizationId) => navigate('admin-organization-detail', 'push', { adminOrganizationId })}
          onOpenProxy={(adminOrganizationId) => navigate('admin-provider-proxy', 'push', { adminOrganizationId })}
          onOpenStockDiagnostics={() => navigate('admin-stock-allocation-diagnostics')}
        />
      )
    case 'admin-stock-allocation-diagnostics':
      return <AdminStockAllocationDiagnosticsPage accessToken={accessToken} onOpenOrganization={(adminOrganizationId) => navigate('admin-organization-detail', 'push', { adminOrganizationId })} />
    case 'admin-scheduled-tasks':
      return <AdminScheduledTasksPage accessToken={accessToken} onOpenStockDiagnostics={() => navigate('admin-stock-allocation-diagnostics')} />
    case 'admin-provider-proxy':
      return <AdminProviderProxyPage accessToken={accessToken} initialOrganizationId={selectedAdminOrganizationId} initialConnectionId={selectedAdminConnectionId} initialSurface={selectedAdminProxySurface} />
    default:
      return null
  }
}

function ShellContent({
  screen,
  navigate,
  accessToken,
  selectedProductId,
  selectedOrderId,
  selectedOrderTab,
  selectedZohoConnectionId,
  selectedZohoPolicyId,
  selectedZohoPullRunId,
  selectedSourceOrderId,
  selectedChannelConnectionId,
  singleItemImportNotice,
  onSingleItemImport,
  onDismissSingleItemImportNotice,
  onOrderSummaryLoaded,
}: {
  screen: Screen
  navigate: NavigateToScreen
  accessToken: string
  selectedProductId: number | null
  selectedOrderId: number | null
  selectedOrderTab: OrderDetailTab
  selectedZohoConnectionId: number | null
  selectedZohoPolicyId: number | null
  selectedZohoPullRunId: number | null
  selectedSourceOrderId: number | null
  selectedChannelConnectionId: number | null
  singleItemImportNotice: InventorySingleItemImportResponse | null
  onSingleItemImport: (response: InventorySingleItemImportResponse) => void
  onDismissSingleItemImportNotice: () => void
  onOrderSummaryLoaded: (summary: OrderSummary) => void
}) {
  const openProduct = (id: number) => {
    onDismissSingleItemImportNotice()
    navigate('product-detail', 'push', { productId: id })
  }
  const openCreate = () => navigate('product-new')
  const handleSaved = (id: number) => navigate('product-detail', 'push', { productId: id })

  switch (screen) {
      case 'home':         return <HomePage accessToken={accessToken} onNavigate={(target) => navigate(target)} onOpenProduct={openProduct} />
    case 'analytics':    return <SalesAnalyticsPage accessToken={accessToken} />
    case 'products':     return <ProductsPage accessToken={accessToken} onOpenDetail={openProduct} onCreateProduct={openCreate} />
    case 'product-detail': return selectedProductId == null ? <ProductsPage accessToken={accessToken} onOpenDetail={openProduct} onCreateProduct={openCreate} /> : <ProductDetailPage accessToken={accessToken} productId={selectedProductId} onBack={() => navigate('products')} onEdit={(id) => navigate('product-edit', 'push', { productId: id })} singleItemImportNotice={singleItemImportNotice} onDismissSingleItemImportNotice={onDismissSingleItemImportNotice} />
    case 'product-new': return <ProductFormPage accessToken={accessToken} mode="create" onCancel={() => navigate('products')} onSaved={handleSaved} />
    case 'product-edit': return <ProductFormPage accessToken={accessToken} mode="edit" productId={selectedProductId} onCancel={() => selectedProductId == null ? navigate('products') : navigate('product-detail', 'push', { productId: selectedProductId })} onSaved={handleSaved} />
    case 'products-empty': return <ProductsEmptyPage onManualCreate={openCreate} />
    case 'orders':       return <OrdersPage accessToken={accessToken} onSummaryLoaded={onOrderSummaryLoaded} onOpenOrder={(orderId) => navigate('order-detail', 'push', { orderId, orderTab: 'overview' })} onOpenOrderRouting={(connectionId, orderId) => navigate('order-routing', 'push', { zohoConnectionId: connectionId, zohoPolicyId: null, sourceOrderId: orderId })} />
    case 'order-detail': return selectedOrderId == null ? <OrdersPage accessToken={accessToken} onOpenOrder={(orderId) => navigate('order-detail', 'push', { orderId, orderTab: 'overview' })} /> : <OrderDetailPage accessToken={accessToken} orderId={selectedOrderId} initialTab={selectedOrderTab} onBack={() => window.history.length > 1 ? window.history.back() : navigate('orders')} onOpenProduct={openProduct} onOpenOrderRouting={(connectionId, orderId) => navigate('order-routing', 'push', { zohoConnectionId: connectionId, sourceOrderId: orderId })} />
    case 'channels':     return <ChannelsPage accessToken={accessToken} onOpenConnection={(channelConnectionId) => navigate('channel-detail', 'push', { channelConnectionId })} onReviewUnmapped={(channelConnectionId) => navigate('unmapped-channel-listings', 'push', { channelConnectionId })} onOpenStockAllocation={(tab) => navigate(tab === 'activity' ? 'stock-allocation-activity' : 'stock-allocation-rules')} />
    case 'channel-detail': return selectedChannelConnectionId == null ? <ChannelsPage accessToken={accessToken} onOpenConnection={(channelConnectionId) => navigate('channel-detail', 'push', { channelConnectionId })} onReviewUnmapped={(channelConnectionId) => navigate('unmapped-channel-listings', 'push', { channelConnectionId })} onOpenStockAllocation={(tab) => navigate(tab === 'activity' ? 'stock-allocation-activity' : 'stock-allocation-rules')} /> : <ChannelConnectionDetailPage accessToken={accessToken} channelConnectionId={selectedChannelConnectionId} onBack={() => navigate('channels')} />
    case 'unmapped-channel-listings': return <ChannelUnmappedListingsPage accessToken={accessToken} channelConnectionId={selectedChannelConnectionId} onBack={() => navigate('channels')} />
    case 'channel-inventory-variance': return <ChannelInventoryVariancePage accessToken={accessToken} onBack={() => navigate('channels')} onOpenProduct={openProduct} onOpenStockAllocation={() => navigate('stock-allocation-rules')} />
    case 'stock-allocation-rules': return <StockAllocationRulesPage accessToken={accessToken} onBack={() => navigate('channels')} onOpenProduct={openProduct} />
    case 'stock-allocation-activity': return <StockAllocationRulesPage accessToken={accessToken} onBack={() => navigate('channels')} initialTab="activity" onOpenProduct={openProduct} />
    case 'inventory':    return <InventoryPage accessToken={accessToken} onOpenTlaaq={() => navigate('tlaaq-inventory-detail')} onOpenZoho={() => navigate('zoho-detail')} />
    case 'tlaaq-inventory-detail': return <TlaaqInventoryDetailPage accessToken={accessToken} onBack={() => navigate('inventory')} onManageWarehouses={() => navigate('warehouses')} onOpenStockAllocation={() => navigate('stock-allocation-rules')} />
    case 'zoho-detail':  return <InventoryZohoDetailPage accessToken={accessToken} onBack={() => navigate('inventory')} onOpenWizard={(connectionId, policyId = null) => navigate('sync-wizard', 'push', { zohoConnectionId: connectionId, zohoPolicyId: policyId, zohoPullRunId: null })} onOpenOrderRouting={(connectionId, policyId = null) => navigate('order-routing', 'push', { zohoConnectionId: connectionId, zohoPolicyId: policyId, zohoPullRunId: null, sourceOrderId: null })} onOpenPullRunDetails={(connectionId, runId) => navigate('zoho-pull-run-detail', 'push', { zohoConnectionId: connectionId, zohoPullRunId: runId, zohoPolicyId: null })} onOpenOrderAudit={(connectionId) => navigate('zoho-order-audit', 'push', { zohoConnectionId: connectionId, zohoPolicyId: null, zohoPullRunId: null })} onOpenProduct={(productId, response) => { onSingleItemImport(response); navigate('product-detail', 'push', { productId }) }} />
    case 'zoho-order-audit': return <InventoryZohoOrderAuditPage accessToken={accessToken} inventoryConnectionId={selectedZohoConnectionId} onBack={() => navigate('zoho-detail')} />
    case 'zoho-pull-run-detail': return <InventoryZohoPullRunDetailPage accessToken={accessToken} inventoryConnectionId={selectedZohoConnectionId} runId={selectedZohoPullRunId} onBack={() => navigate('zoho-detail')} />
    case 'sync-wizard':  return <SyncRuleWizardPage accessToken={accessToken} connectionId={selectedZohoConnectionId} policyId={selectedZohoPolicyId} onBack={() => navigate('zoho-detail')} />
    case 'order-routing': return <OrderRoutingPage key={`${selectedZohoConnectionId ?? 'none'}:${selectedZohoPolicyId ?? 'new'}:${selectedSourceOrderId ?? 'none'}`} accessToken={accessToken} connectionId={selectedZohoConnectionId} policyId={selectedZohoPolicyId} sourceOrderId={selectedSourceOrderId} onBack={() => navigate(selectedSourceOrderId ? 'orders' : 'zoho-detail')} />
    case 'warehouses':   return <WarehousesPage accessToken={accessToken} />
    case 'team':         return <TeamPage accessToken={accessToken} />
    default:             return null
  }
}

function normalizePath(path: string): string {
  return path.replace(/\/+$/, '') || '/'
}

function parsePositiveId(value: string | undefined): number | null {
  if (!value || !/^\d+$/.test(value)) return null
  const parsed = Number(value)
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null
}

function routeFromPath(path: string): AppRoute | null {
  const url = new URL(path, 'http://tlaaq.local')
  const normalizedPath = normalizePath(url.pathname)
  const [, first, second, third, fourth, fifth] = normalizedPath.split('/')

  if (first === 'admin' && second === 'organizations' && fourth === undefined) {
    const adminOrganizationId = parsePositiveId(third)
    if (adminOrganizationId != null) return { screen: 'admin-organization-detail', adminOrganizationId }
  }

  if (first === 'products' && third === undefined) {
    const productId = parsePositiveId(second)
    if (productId != null) return { screen: 'product-detail', productId }
  }

  if (first === 'orders' && third === undefined) {
    const orderId = parsePositiveId(second)
    const rawTab = url.searchParams.get('tab')
    const orderTab: OrderDetailTab = rawTab === 'noon' || rawTab === 'zoho' || rawTab === 'activity' ? rawTab : 'overview'
    if (orderId != null) return { screen: 'order-detail', orderId, orderTab }
  }

  if (first === 'products' && third === 'edit' && fourth === undefined) {
    const productId = parsePositiveId(second)
    if (productId != null) return { screen: 'product-edit', productId }
  }

  if (first === 'inventory' && second === 'zoho' && fourth === 'sync-rule') {
    const zohoConnectionId = parsePositiveId(third)
    const zohoPolicyId = parsePositiveId(fifth)
    if (zohoConnectionId != null && (fifth === undefined || zohoPolicyId != null)) return { screen: 'sync-wizard', zohoConnectionId, zohoPolicyId }
  }

  if (first === 'inventory' && second === 'zoho' && fourth === 'order-routing') {
    const zohoConnectionId = parsePositiveId(third)
    const zohoPolicyId = parsePositiveId(fifth)
    const sourceOrderId = parsePositiveId(url.searchParams.get('orderId') ?? undefined)
    if (zohoConnectionId != null && (fifth === undefined || zohoPolicyId != null)) return { screen: 'order-routing', zohoConnectionId, zohoPolicyId, sourceOrderId }
  }

  if (first === 'inventory' && second === 'zoho' && fourth === 'pull-runs' && fifth !== undefined) {
    const zohoConnectionId = parsePositiveId(third)
    const zohoPullRunId = parsePositiveId(fifth)
    if (zohoConnectionId != null && zohoPullRunId != null) return { screen: 'zoho-pull-run-detail', zohoConnectionId, zohoPullRunId }
  }

  if (first === 'inventory' && second === 'zoho' && fourth === 'order-audit' && fifth === undefined) {
    const zohoConnectionId = parsePositiveId(third)
    if (zohoConnectionId != null) return { screen: 'zoho-order-audit', zohoConnectionId }
  }

  if (first === 'channels' && third === 'unmapped-listings' && fourth === undefined) {
    const channelConnectionId = parsePositiveId(second)
    if (channelConnectionId != null) return { screen: 'unmapped-channel-listings', channelConnectionId }
  }

  if (first === 'channels' && third === undefined) {
    const channelConnectionId = parsePositiveId(second)
    if (channelConnectionId != null) return { screen: 'channel-detail', channelConnectionId }
  }

  const staticScreen = STATIC_PATH_SCREENS[normalizedPath]
  if (staticScreen === 'admin-provider-proxy') {
    const adminOrganizationId = parsePositiveId(url.searchParams.get('organizationId') ?? undefined)
    const adminConnectionId = parsePositiveId(url.searchParams.get('connectionId') ?? undefined)
    const surface = url.searchParams.get('surface')
    const adminProxySurface: AdminProviderProxySurface | null = surface === 'channel' || surface === 'inventory' ? surface : null
    return { screen: staticScreen, adminOrganizationId, adminConnectionId, adminProxySurface }
  }
  return staticScreen ? { screen: staticScreen } : null
}

function pathForScreen(screen: Screen, params: RouteParams = {}): string | null {
  switch (screen) {
    case 'product-detail':
      return params.productId ? `/products/${params.productId}` : '/products'
    case 'order-detail':
      return params.orderId ? `/orders/${params.orderId}?tab=${params.orderTab ?? 'overview'}` : '/orders'
    case 'product-edit':
      return params.productId ? `/products/${params.productId}/edit` : '/products'
    case 'sync-wizard':
      if (!params.zohoConnectionId) return '/inventory/zoho'
      return params.zohoPolicyId ? `/inventory/zoho/${params.zohoConnectionId}/sync-rule/${params.zohoPolicyId}` : `/inventory/zoho/${params.zohoConnectionId}/sync-rule`
    case 'order-routing': {
      if (!params.zohoConnectionId) return '/inventory/zoho'
      const base = params.zohoPolicyId ? `/inventory/zoho/${params.zohoConnectionId}/order-routing/${params.zohoPolicyId}` : `/inventory/zoho/${params.zohoConnectionId}/order-routing`
      return params.sourceOrderId ? `${base}?orderId=${params.sourceOrderId}` : base
    }
    case 'zoho-pull-run-detail':
      return params.zohoConnectionId && params.zohoPullRunId ? `/inventory/zoho/${params.zohoConnectionId}/pull-runs/${params.zohoPullRunId}` : '/inventory/zoho'
    case 'zoho-order-audit':
      return params.zohoConnectionId ? `/inventory/zoho/${params.zohoConnectionId}/order-audit` : '/inventory/zoho'
    case 'channel-detail':
      return params.channelConnectionId ? `/channels/${params.channelConnectionId}` : '/channels'
    case 'unmapped-channel-listings':
      return params.channelConnectionId ? `/channels/${params.channelConnectionId}/unmapped-listings` : '/channels'
    case 'channel-inventory-variance':
      return '/channels/inventory-variance'
    case 'stock-allocation-rules':
      return '/channels/stock-allocation-rules'
    case 'stock-allocation-activity':
      return '/channels/stock-allocation-activity'
    case 'admin-organization-detail':
      return params.adminOrganizationId ? `/admin/organizations/${params.adminOrganizationId}` : '/admin/organizations'
    case 'admin-provider-proxy': {
      const search = new URLSearchParams()
      if (params.adminOrganizationId) search.set('organizationId', String(params.adminOrganizationId))
      if (params.adminConnectionId) search.set('connectionId', String(params.adminConnectionId))
      if (params.adminProxySurface) search.set('surface', params.adminProxySurface)
      const query = search.toString()
      return query ? `/admin/provider-proxy?${query}` : '/admin/provider-proxy'
    }
    default:
      return STATIC_SCREEN_PATHS[screen] ?? null
  }
}

function initialAuthenticatedRoute(route: AppRoute | null, user?: AuthUser): AppRoute {
  const admin = isAdminUser(user)
  if (!route || route.screen === 'landing' || AUTH_SCREENS.has(route.screen)) return { screen: admin ? 'admin-home' : 'home' }
  if (PUBLIC_SCREENS.has(route.screen)) return route
  if (admin && !isAdminScreen(route.screen)) return { screen: 'admin-home' }
  if (!admin && isAdminScreen(route.screen)) return { screen: 'home' }

  switch (route.screen) {
    case 'product-detail':
    case 'product-edit':
      return route.productId ? route : { screen: 'products' }
    case 'order-detail':
      return route.orderId ? route : { screen: 'orders' }
    case 'sync-wizard':
      return route.zohoConnectionId ? route : { screen: 'zoho-detail' }
    case 'zoho-pull-run-detail':
      return route.zohoConnectionId && route.zohoPullRunId ? route : { screen: 'zoho-detail' }
    case 'zoho-order-audit':
      return route.zohoConnectionId ? route : { screen: 'zoho-detail' }
    case 'channel-detail':
    case 'unmapped-channel-listings':
      return route.channelConnectionId ? route : { screen: 'channels' }
    case 'admin-organization-detail':
      return route.adminOrganizationId ? route : { screen: 'admin-organizations' }
    default:
      return route
  }
}

function routeParams(route: AppRoute): RouteParams {
  return {
    orderId: route.orderId,
    orderTab: route.orderTab,
    productId: route.productId,
    zohoConnectionId: route.zohoConnectionId,
    zohoPolicyId: route.zohoPolicyId,
    zohoPullRunId: route.zohoPullRunId,
    sourceOrderId: route.sourceOrderId,
    channelConnectionId: route.channelConnectionId,
    adminOrganizationId: route.adminOrganizationId,
    adminConnectionId: route.adminConnectionId,
    adminProxySurface: route.adminProxySurface,
  }
}

function resetPasswordUrlState(): { isResetPath: boolean; token: string | null } {
  const url = new URL(window.location.href)
  const normalizedPath = normalizePath(url.pathname)
  return {
    isResetPath: normalizedPath === '/reset-password',
    token: url.searchParams.get('token'),
  }
}

function invitationUrlState(): { isInvitationPath: boolean; token: string | null } {
  const url = new URL(window.location.href)
  const normalizedPath = normalizePath(url.pathname)
  return {
    isInvitationPath: normalizedPath === '/accept-invitation',
    token: url.searchParams.get('token'),
  }
}

function currentPath(): string {
  if (typeof window === 'undefined') return '/'
  const url = new URL(window.location.href)
  return normalizePath(url.pathname)
}

function currentBrowserUrl(): string {
  if (typeof window === 'undefined') return '/'
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

function initialOAuthScreen(): OAuthScreen | null {
  const path = currentPath()

  if (path === '/zid-oauth') {
    captureZidOAuthCallbackFromUrl()
    clearAmazonOAuthCallback()
    clearSallaOAuthCallback()
    clearZohoOAuthCallback()
    return 'zid-oauth'
  }

  if (path === '/salla-oauth') {
    captureSallaOAuthCallbackFromUrl()
    clearAmazonOAuthCallback()
    clearZidOAuthCallback()
    clearZohoOAuthCallback()
    return 'salla-oauth'
  }

  if (path === '/amazon-oauth') {
    captureAmazonOAuthCallbackFromUrl()
    clearZidOAuthCallback()
    clearSallaOAuthCallback()
    clearZohoOAuthCallback()
    return 'amazon-oauth'
  }

  if (path === '/zoho-oauth') {
    captureZohoOAuthCallbackFromUrl()
    clearAmazonOAuthCallback()
    clearZidOAuthCallback()
    clearSallaOAuthCallback()
    return 'zoho-oauth'
  }

  if (hasStoredZidOAuthCallback()) return 'zid-oauth'
  if (hasStoredSallaOAuthCallback()) return 'salla-oauth'
  if (hasStoredAmazonOAuthCallback()) return 'amazon-oauth'
  if (hasStoredZohoOAuthCallback()) return 'zoho-oauth'
  return null
}

export default function App() {
  const { t } = useTranslation()
  const [initialResetState] = useState(() => resetPasswordUrlState())
  const [initialInvitationState] = useState(() => invitationUrlState())
  const [initialRoute] = useState(() => routeFromPath(currentBrowserUrl()))
  const initialPathScreen = initialRoute?.screen ?? null
  const [initialSallaInvitation] = useState(captureSallaInvitation)
  const [sallaInvitationToken, setSallaInvitationToken] = useState(initialSallaInvitation)
  const [initialOAuth] = useState(() => initialOAuthScreen())
  const initialZidOAuth = initialOAuth === 'zid-oauth'
  const initialSallaOAuth = initialOAuth === 'salla-oauth'
  const initialAmazonOAuth = initialOAuth === 'amazon-oauth'
  const initialZohoOAuth = initialOAuth === 'zoho-oauth'
  const [screen, setScreen] = useState<Screen>(() => {
    if (initialInvitationState.isInvitationPath) return 'accept-invitation'
    if (initialResetState.isResetPath) return 'reset-password'
    if (initialPathScreen === 'salla-invitation') return 'salla-invitation'
    if (initialZidOAuth) return 'zid-oauth'
    if (initialSallaOAuth) return 'salla-oauth'
    if (initialAmazonOAuth) return 'amazon-oauth'
    if (initialZohoOAuth) return 'zoho-oauth'
    return initialPathScreen ?? 'landing'
  })
  const [auth, setAuth] = useState<AuthState>(initialResetState.isResetPath || initialInvitationState.isInvitationPath ? { status: 'anonymous' } : { status: 'checking' })
  const [resetToken, setResetToken] = useState<string | null>(initialResetState.token)
  const [invitationToken, setInvitationToken] = useState<string | null>(initialInvitationState.token)
  const [selectedProductId, setSelectedProductId] = useState<number | null>(initialRoute?.productId ?? null)
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(initialRoute?.orderId ?? null)
  const [selectedOrderTab, setSelectedOrderTab] = useState<OrderDetailTab>(initialRoute?.orderTab ?? 'overview')
  const [singleItemImportNotice, setSingleItemImportNotice] = useState<InventorySingleItemImportResponse | null>(null)
  const [selectedZohoConnectionId, setSelectedZohoConnectionId] = useState<number | null>(initialRoute?.zohoConnectionId ?? null)
  const [selectedZohoPolicyId, setSelectedZohoPolicyId] = useState<number | null>(initialRoute?.zohoPolicyId ?? null)
  const [selectedZohoPullRunId, setSelectedZohoPullRunId] = useState<number | null>(initialRoute?.zohoPullRunId ?? null)
  const [selectedSourceOrderId, setSelectedSourceOrderId] = useState<number | null>(initialRoute?.sourceOrderId ?? null)
  const [selectedChannelConnectionId, setSelectedChannelConnectionId] = useState<number | null>(initialRoute?.channelConnectionId ?? null)
  const [selectedAdminOrganizationId, setSelectedAdminOrganizationId] = useState<number | null>(initialRoute?.adminOrganizationId ?? null)
  const [selectedAdminConnectionId, setSelectedAdminConnectionId] = useState<number | null>(initialRoute?.adminConnectionId ?? null)
  const [selectedAdminProxySurface, setSelectedAdminProxySurface] = useState<AdminProviderProxySurface | null>(initialRoute?.adminProxySurface ?? null)
  const selectedRouteParamsRef = useRef<RouteParams>({
    orderId: initialRoute?.orderId ?? null,
    orderTab: initialRoute?.orderTab ?? 'overview',
    productId: initialRoute?.productId ?? null,
    zohoConnectionId: initialRoute?.zohoConnectionId ?? null,
    zohoPolicyId: initialRoute?.zohoPolicyId ?? null,
    zohoPullRunId: initialRoute?.zohoPullRunId ?? null,
    sourceOrderId: initialRoute?.sourceOrderId ?? null,
    channelConnectionId: initialRoute?.channelConnectionId ?? null,
    adminOrganizationId: initialRoute?.adminOrganizationId ?? null,
    adminConnectionId: initialRoute?.adminConnectionId ?? null,
    adminProxySurface: initialRoute?.adminProxySurface ?? null,
  })
  const [orderNewCount, setOrderNewCount] = useState<number | null>(null)

  const handleOrderSummaryLoaded = useCallback((summary: OrderSummary) => {
    setOrderNewCount(summary.byStatus?.NEW ?? 0)
  }, [])

  useEffect(() => {
    selectedRouteParamsRef.current = {
      orderId: selectedOrderId,
      orderTab: selectedOrderTab,
      productId: selectedProductId,
      zohoConnectionId: selectedZohoConnectionId,
      zohoPolicyId: selectedZohoPolicyId,
      zohoPullRunId: selectedZohoPullRunId,
      sourceOrderId: selectedSourceOrderId,
      channelConnectionId: selectedChannelConnectionId,
      adminOrganizationId: selectedAdminOrganizationId,
      adminConnectionId: selectedAdminConnectionId,
      adminProxySurface: selectedAdminProxySurface,
    }
  }, [selectedAdminConnectionId, selectedAdminOrganizationId, selectedAdminProxySurface, selectedChannelConnectionId, selectedOrderId, selectedOrderTab, selectedProductId, selectedSourceOrderId, selectedZohoConnectionId, selectedZohoPolicyId, selectedZohoPullRunId])

  const navigateToScreen = useCallback<NavigateToScreen>((nextScreen, historyMode = 'push', params = {}) => {
    const currentParams = selectedRouteParamsRef.current
    const pathParams: RouteParams = {
      orderId: params.orderId !== undefined ? params.orderId : currentParams.orderId,
      orderTab: params.orderTab !== undefined ? params.orderTab : currentParams.orderTab,
      productId: params.productId !== undefined ? params.productId : currentParams.productId,
      zohoConnectionId: params.zohoConnectionId !== undefined ? params.zohoConnectionId : currentParams.zohoConnectionId,
      zohoPolicyId: params.zohoPolicyId !== undefined ? params.zohoPolicyId : currentParams.zohoPolicyId,
      zohoPullRunId: params.zohoPullRunId !== undefined ? params.zohoPullRunId : currentParams.zohoPullRunId,
      sourceOrderId: params.sourceOrderId !== undefined ? params.sourceOrderId : currentParams.sourceOrderId,
      channelConnectionId: params.channelConnectionId !== undefined ? params.channelConnectionId : currentParams.channelConnectionId,
      adminOrganizationId: params.adminOrganizationId !== undefined ? params.adminOrganizationId : currentParams.adminOrganizationId,
      adminConnectionId: params.adminConnectionId !== undefined ? params.adminConnectionId : currentParams.adminConnectionId,
      adminProxySurface: params.adminProxySurface !== undefined ? params.adminProxySurface : currentParams.adminProxySurface,
    }
    const nextParams: RouteParams = { ...pathParams }

    setScreen(nextScreen)
    if (nextScreen !== 'product-detail') setSingleItemImportNotice(null)

    if (params.productId !== undefined) setSelectedProductId(params.productId)
    if (params.orderId !== undefined) setSelectedOrderId(params.orderId)
    if (params.orderTab !== undefined && params.orderTab !== null) setSelectedOrderTab(params.orderTab)
    if (params.zohoConnectionId !== undefined) setSelectedZohoConnectionId(params.zohoConnectionId)
    if (params.zohoPolicyId !== undefined) setSelectedZohoPolicyId(params.zohoPolicyId)
    if (params.zohoPullRunId !== undefined) setSelectedZohoPullRunId(params.zohoPullRunId)
    if (params.sourceOrderId !== undefined) setSelectedSourceOrderId(params.sourceOrderId)
    if (params.channelConnectionId !== undefined) setSelectedChannelConnectionId(params.channelConnectionId)
    if (params.adminOrganizationId !== undefined) setSelectedAdminOrganizationId(params.adminOrganizationId)
    if (params.adminConnectionId !== undefined) setSelectedAdminConnectionId(params.adminConnectionId)
    if (params.adminProxySurface !== undefined) setSelectedAdminProxySurface(params.adminProxySurface)
    if (nextScreen === 'products' || nextScreen === 'product-new' || nextScreen === 'products-empty') {
      nextParams.productId = null
      setSelectedProductId(null)
    }
    if (nextScreen === 'channels') {
      nextParams.channelConnectionId = null
      setSelectedChannelConnectionId(null)
    }
    if (nextScreen === 'inventory' || nextScreen === 'zoho-detail') {
      nextParams.zohoConnectionId = null
      nextParams.zohoPolicyId = null
      nextParams.zohoPullRunId = null
      setSelectedZohoConnectionId(null)
      setSelectedZohoPolicyId(null)
      setSelectedZohoPullRunId(null)
    }
    if (nextScreen === 'sync-wizard') {
      nextParams.zohoPullRunId = null
      setSelectedZohoPullRunId(null)
    }
    if (nextScreen === 'admin-home' || nextScreen === 'admin-organizations') {
      nextParams.adminOrganizationId = null
      nextParams.adminConnectionId = null
      nextParams.adminProxySurface = null
      setSelectedAdminOrganizationId(null)
      setSelectedAdminConnectionId(null)
      setSelectedAdminProxySurface(null)
    }
    if (nextScreen === 'admin-organization-detail') {
      nextParams.adminConnectionId = null
      nextParams.adminProxySurface = null
      setSelectedAdminConnectionId(null)
      setSelectedAdminProxySurface(null)
    }
    selectedRouteParamsRef.current = nextParams

    const path = pathForScreen(nextScreen, pathParams)
    if (!path || currentBrowserUrl() === path) return
    // OrdersPage owns its list filters; keep them intact during auth bootstrap.
    if (historyMode === 'replace' && nextScreen === 'orders' && currentPath() === '/orders') return

    if (historyMode === 'replace') {
      window.history.replaceState({}, '', path)
      return
    }

    window.history.pushState({}, '', path)
  }, [])

  useEffect(() => {
    function handlePopState() {
      const route = routeFromPath(currentBrowserUrl()) ?? { screen: 'landing' }
      setScreen(route.screen)
      const params = routeParams(route)
      selectedRouteParamsRef.current = params
      setSelectedProductId(params.productId ?? null)
      setSelectedOrderId(params.orderId ?? null)
      setSelectedOrderTab(params.orderTab ?? 'overview')
      setSelectedZohoConnectionId(params.zohoConnectionId ?? null)
      setSelectedZohoPolicyId(params.zohoPolicyId ?? null)
      setSelectedZohoPullRunId(params.zohoPullRunId ?? null)
      setSelectedSourceOrderId(params.sourceOrderId ?? null)
      setSelectedChannelConnectionId(params.channelConnectionId ?? null)
      setSelectedAdminOrganizationId(params.adminOrganizationId ?? null)
      setSelectedAdminConnectionId(params.adminConnectionId ?? null)
      setSelectedAdminProxySurface(params.adminProxySurface ?? null)
      setSingleItemImportNotice(null)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  useEffect(() => {
    if (initialInvitationState.isInvitationPath) {
      if (initialInvitationState.token) {
        window.history.replaceState({}, '', '/accept-invitation')
      }
      return
    }

    if (initialResetState.isResetPath) {
      if (initialResetState.token) {
        window.history.replaceState({}, '', '/reset-password')
      }
      return
    }

    let cancelled = false
    refresh()
      .then(response => {
        if (cancelled) return
        setLocale(response.user.locale)
        setAuth({ status: 'authenticated', accessToken: response.accessToken, user: response.user })
        if (initialSallaInvitation && !initialInvitationState.isInvitationPath) {
          navigateToScreen('salla-invitation', 'replace')
          return
        }
        // Preserve OAuth landing screens so callback pages can finish the
        // callback POST. Other initial paths restore the screen encoded in the URL.
        if (!initialZidOAuth && !initialSallaOAuth && !initialAmazonOAuth && !initialZohoOAuth) {
          const initialAuthenticated = initialAuthenticatedRoute(initialRoute, response.user)
          navigateToScreen(initialAuthenticated.screen, 'replace', routeParams(initialAuthenticated))
        }
      })
      .catch(() => {
        if (cancelled) return
        setAuth({ status: 'anonymous' })
        setOrderNewCount(null)
        if (initialPathScreen === 'salla-invitation') {
          setScreen('salla-invitation')
          return
        }
        if (initialPathScreen === 'landing' || (initialPathScreen && PUBLIC_SCREENS.has(initialPathScreen))) {
          setScreen(initialPathScreen)
          return
        }
        setScreen(initialPathScreen && AUTH_SCREENS.has(initialPathScreen) ? initialPathScreen : 'login')
      })

    return () => {
      cancelled = true
    }
  }, [initialInvitationState.isInvitationPath, initialInvitationState.token, initialPathScreen, initialResetState.isResetPath, initialResetState.token, initialAmazonOAuth, initialSallaOAuth, initialZidOAuth, initialZohoOAuth, initialRoute, navigateToScreen, initialSallaInvitation])

  useLayoutEffect(() => {
    if (auth.status !== 'authenticated') {
      setSessionRefreshHandler(null)
      return
    }

    let active = true
    setSessionRefreshHandler<AuthResponse>({
      refresh: () => refresh(),
      onRefreshSuccess: response => {
        if (!active) return
        setLocale(response.user.locale)
        setAuth({ status: 'authenticated', accessToken: response.accessToken, user: response.user })
      },
      onRefreshFailure: () => {
        if (!active) return
        setAuth({ status: 'anonymous' })
        setOrderNewCount(null)
        setResetToken(null)
        setInvitationToken(null)
        navigateToScreen('login', 'replace')
      },
    })

    return () => {
      active = false
      setSessionRefreshHandler(null)
    }
  }, [auth.status, navigateToScreen])

  const authenticatedAccessToken = auth.status === 'authenticated' ? auth.accessToken : null
  const authenticatedUser = auth.status === 'authenticated' ? auth.user : null

  useEffect(() => {
    if (!authenticatedAccessToken || isAdminUser(authenticatedUser)) return

    let cancelled = false

    getOrderSummary(authenticatedAccessToken)
      .then(summary => {
        if (!cancelled) handleOrderSummaryLoaded(summary)
      })
      .catch(() => {
        if (!cancelled) setOrderNewCount(null)
      })

    return () => {
      cancelled = true
    }
  }, [authenticatedAccessToken, authenticatedUser, handleOrderSummaryLoaded])

  useEffect(() => {
    if (auth.status !== 'authenticated') return
    if (screen === 'salla-invitation') return
    if (PUBLIC_SCREENS.has(screen)) return
    void Promise.resolve().then(() => {
      if (isAdminUser(auth.user) && !isAdminScreen(screen)) {
        navigateToScreen('admin-home', 'replace')
        return
      }
      if (!isAdminUser(auth.user) && (isAdminScreen(screen) || screen === 'landing' || AUTH_SCREENS.has(screen))) {
        navigateToScreen('home', 'replace')
      }
    })
  }, [auth, navigateToScreen, screen])

  function enterAuthenticated(response: AuthResponse) {
    setOrderNewCount(null)
    setLocale(response.user.locale)
    setAuth({ status: 'authenticated', accessToken: response.accessToken, user: response.user })
    setResetToken(null)
    setInvitationToken(null)
    if (sallaInvitationToken && !initialInvitationState.isInvitationPath) {
      navigateToScreen('salla-invitation', 'replace')
      return
    }
    // If the user landed on an OAuth callback and had to sign in first, keep
    // them on that screen so the callback can be POSTed once authentication completes.
    // Invitation acceptance is a separate auth handoff and must not be hijacked
    // by stale OAuth callbacks left in session storage.
    if (!initialInvitationState.isInvitationPath) {
      if (initialZidOAuth || hasStoredZidOAuthCallback()) {
        navigateToScreen('zid-oauth', 'replace')
        return
      }
      if (initialSallaOAuth || hasStoredSallaOAuthCallback()) {
        navigateToScreen('salla-oauth', 'replace')
        return
      }
      if (initialAmazonOAuth || hasStoredAmazonOAuthCallback()) {
        navigateToScreen('amazon-oauth', 'replace')
        return
      }
      if (initialZohoOAuth || hasStoredZohoOAuthCallback()) {
        navigateToScreen('zoho-oauth', 'replace')
        return
      }
    }
    const initialAuthenticated = initialAuthenticatedRoute(initialRoute, response.user)
    navigateToScreen(initialAuthenticated.screen, 'replace', routeParams(initialAuthenticated))
  }

  function handleLogout() {
    void logout().catch(() => undefined).finally(() => {
      setAuth({ status: 'anonymous' })
      setOrderNewCount(null)
      setResetToken(null)
      setInvitationToken(null)
      navigateToScreen('login', 'replace')
    })
  }

  const navigatePublic = (destination: PublicDestination) => navigateToScreen(destination)

  if (screen === 'privacy') return <PublicLegalPage page="privacy" onNavigate={navigatePublic} />
  if (screen === 'terms') return <PublicLegalPage page="terms" onNavigate={navigatePublic} />
  if (screen === 'faq') return <FaqPage onNavigate={navigatePublic} />
  if (screen === 'support') return <SupportPage onNavigate={navigatePublic} />

  if (auth.status === 'checking') {
    return (
      <AuthLayout eyebrow={t('app.loading.eyebrow')} title={t('app.loading.title')} subtitle={t('app.loading.subtitle')}>
        <div style={{ padding: '12px 14px', borderRadius: 12, background: 'var(--ink-50)', color: 'var(--fg-muted)', fontSize: 14, fontWeight: 700 }}>
          {t('app.loading.body')}
        </div>
      </AuthLayout>
    )
  }

  const visibleScreen: Screen = auth.status === 'authenticated' && (screen === 'landing' || AUTH_SCREENS.has(screen)) ? 'home' : screen

  const authContext = initialZidOAuth ? 'zid-oauth' : initialSallaOAuth ? 'salla-oauth' : initialAmazonOAuth ? 'amazon-oauth' : initialZohoOAuth ? 'zoho-oauth' : undefined

  if (visibleScreen === 'landing') {
    return <LandingPage onSignIn={() => navigateToScreen('login')} onRegister={() => navigateToScreen('register')} onPublicNavigate={navigatePublic} />
  }
  if (visibleScreen === 'login') {
    return <LoginPage authContext={authContext} onLoginSuccess={enterAuthenticated} onRegister={() => navigateToScreen('register')} onForgotPassword={() => navigateToScreen('forgot-password')} onHome={() => navigateToScreen('landing')} />
  }
  if (visibleScreen === 'register') {
    return <RegisterPage authContext={authContext} onRegisterSuccess={enterAuthenticated} onLogin={() => navigateToScreen('login')} onHome={() => navigateToScreen('landing')} onTerms={() => navigateToScreen('terms')} onPrivacy={() => navigateToScreen('privacy')} />
  }
  if (visibleScreen === 'forgot-password') {
    return <ForgotPasswordPage onBackToLogin={() => navigateToScreen('login')} />
  }
  if (visibleScreen === 'reset-password') {
    return <ResetPasswordPage token={resetToken} onResetSuccess={enterAuthenticated} onRequestNewLink={() => navigateToScreen('forgot-password', 'replace')} />
  }
  if (visibleScreen === 'accept-invitation') {
    return (
      <AcceptInvitationPage
        token={invitationToken}
        onAcceptSuccess={enterAuthenticated}
        onPrivacy={() => navigateToScreen('privacy')}
        onBackToLogin={() => {
          navigateToScreen('login', 'replace')
          setInvitationToken(null)
        }}
      />
    )
  }

  if (visibleScreen === 'salla-invitation') {
    return <SallaInvitationPage
      token={sallaInvitationToken}
      session={auth.status === 'authenticated' ? auth : null}
      onLogin={() => auth.status === 'authenticated' ? handleLogout() : navigateToScreen('login')}
      onRegister={() => navigateToScreen('register')}
      onDone={() => {
        clearSallaInvitation()
        setSallaInvitationToken(null)
        navigateToScreen(auth.status === 'authenticated' ? 'channels' : 'login', 'replace')
      }}
    />
  }

  if (auth.status !== 'authenticated') {
    return <LoginPage authContext={authContext} onLoginSuccess={enterAuthenticated} onRegister={() => navigateToScreen('register')} onForgotPassword={() => navigateToScreen('forgot-password')} onHome={() => navigateToScreen('landing')} />
  }

  const pendingRoleRedirect = (isAdminUser(auth.user) && !isAdminScreen(visibleScreen)) || (!isAdminUser(auth.user) && isAdminScreen(visibleScreen))
  if (pendingRoleRedirect) {
    return (
      <AuthLayout eyebrow={t('app.loading.eyebrow')} title={t('app.loading.title')} subtitle={t('app.loading.subtitle')}>
        <div style={{ padding: '12px 14px', borderRadius: 12, background: 'var(--ink-50)', color: 'var(--fg-muted)', fontSize: 14, fontWeight: 700 }}>
          {t('app.loading.body')}
        </div>
      </AuthLayout>
    )
  }

  if (isAdminUser(auth.user) && isAdminScreen(visibleScreen)) {
    const adminMeta = ADMIN_META[visibleScreen]
    return (
      <AdminShell
        active={activeAdminPage(visibleScreen)}
        title={t(adminMeta.titleKey)}
        subtitle={adminMeta.subtitleKey ? t(adminMeta.subtitleKey) : undefined}
        viewer={auth.user}
        onNavigate={(page) => navigateToScreen(page)}
        onLogout={handleLogout}
        onLocaleChange={persistLocale}
      >
        <AdminContent
          screen={visibleScreen}
          navigate={navigateToScreen}
          accessToken={auth.accessToken}
          selectedAdminOrganizationId={selectedAdminOrganizationId}
          selectedAdminConnectionId={selectedAdminConnectionId}
          selectedAdminProxySurface={selectedAdminProxySurface}
        />
      </AdminShell>
    )
  }

  // Standalone screens (no app shell)
  if (visibleScreen === 'onboarding') {
    return <OnboardingPage onComplete={() => navigateToScreen('home', 'replace')} />
  }
  if (visibleScreen === 'zid-oauth') {
    return (
      <ZidOAuthPage
        accessToken={auth.accessToken}
        onDone={() => navigateToScreen('channels', 'replace')}
      />
    )
  }
  if (visibleScreen === 'salla-oauth') {
    return (
      <SallaOAuthPage
        accessToken={auth.accessToken}
        onDone={() => navigateToScreen('channels', 'replace')}
      />
    )
  }
  if (visibleScreen === 'amazon-oauth') {
    return (
      <AmazonOAuthPage
        accessToken={auth.accessToken}
        onDone={() => navigateToScreen('channels', 'replace')}
      />
    )
  }
  if (visibleScreen === 'zoho-oauth') {
    return (
      <ZohoOAuthPage
        accessToken={auth.accessToken}
        onDone={() => navigateToScreen('zoho-detail', 'replace')}
      />
    )
  }

  const shellScreen = SHELL_SCREENS.has(visibleScreen) ? visibleScreen : 'home'
  const navPage = activeNavPage(shellScreen)
  const meta = SUB_META[shellScreen] ?? PAGE_META[navPage]
  const badgeKey = PAGE_META[navPage].badgeKey
  const badge = navPage === 'orders' && orderNewCount != null
    ? t('app.meta.orders.newBadge', { count: orderNewCount })
    : badgeKey ? t(badgeKey) : undefined
  const navCounts = orderNewCount == null ? undefined : { orders: orderNewCount }

  async function persistLocale(locale: Locale) {
    if (auth.status !== 'authenticated') return
    try {
      const user = await updateLocale(auth.accessToken, locale)
      setAuth(current => current.status === 'authenticated' ? { ...current, user } : current)
    } catch (error) {
      console.warn('auth-locale-sync-failed', error)
    }
  }

  return (
    <SharedImportsProvider accessToken={auth.accessToken} onReviewUnmapped={channelConnectionId => navigateToScreen('unmapped-channel-listings', 'push', { channelConnectionId })}>
    <ImportsAppShell
      active={navPage}
      onNavigate={(p) => {
        if (p === 'products') setSelectedProductId(null)
        navigateToScreen(p)
      }}
      title={t(meta.titleKey)}
      subtitle={meta.subtitleKey ? t(meta.subtitleKey) : undefined}
      badge={badge}
      viewer={auth.user}
      onLogout={handleLogout}
      onLocaleChange={persistLocale}
      navCounts={navCounts}
    >
      <ShellContent
        screen={shellScreen}
        navigate={navigateToScreen}
        accessToken={auth.accessToken}
        selectedProductId={selectedProductId}
        selectedOrderId={selectedOrderId}
        selectedOrderTab={selectedOrderTab}
        selectedZohoConnectionId={selectedZohoConnectionId}
        selectedZohoPolicyId={selectedZohoPolicyId}
        selectedZohoPullRunId={selectedZohoPullRunId}
        selectedSourceOrderId={selectedSourceOrderId}
        selectedChannelConnectionId={selectedChannelConnectionId}
        singleItemImportNotice={singleItemImportNotice}
        onSingleItemImport={setSingleItemImportNotice}
        onDismissSingleItemImportNotice={() => setSingleItemImportNotice(null)}
        onOrderSummaryLoaded={handleOrderSummaryLoaded}
      />
    </ImportsAppShell>
    </SharedImportsProvider>
  )
}

function ImportsAppShell(props: ComponentProps<typeof AppShell>) {
  const imports = useSharedImports()
  return <AppShell {...props} onOpenImports={imports.openAll} importsRunningCount={imports.runningCount} importsAttentionCount={imports.attentionCount} />
}
